import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Alert, Button, Form, Input, Select, message, Row, Col, Checkbox } from "antd";
import ActiveStatusFormItem from 'app/shared/components/Form/ActiveStatusFormItem';
import { getAccountCategories, getProductCategories, getTransferById, getTransferPorts, getTransferTypes } from "../selectors";
import { UpdateDraftTransferProduct, listTransferssProductsData } from "../actions";
import * as api from "app/shared/api";
import { DivisionId } from "app/shared/division/getDivisionId";
import { PRODUCT_ACTIVE_PACKAGE_TOOLTIP } from "app/shared/api/productDtoHelpers";
import { useActivePackageDeactivationLock } from "../hooks/useActivePackageDeactivationLock";
import {
  TransfersProduct,
  BookingSystem
} from "app/shared/model";

interface TransferFormProps {
  onSuccess?: () => void;
}

const TRANSFER_PRODUCT_TYPE_ID = 4;

const AddEditTransferForm: React.FC<TransferFormProps> = ({ onSuccess }) => {
  const dispatch = useDispatch();
  const transferProduct = useSelector(getTransferById);
  const transferTypes = useSelector(getTransferTypes);
  const transferPorts = useSelector(getTransferPorts);
  const productCategories = useSelector(getProductCategories);
  const accountCategories = useSelector(getAccountCategories);
  const [form] = Form.useForm();
  const [isLoading, setIsLoading] = React.useState(false);
  const [productUpdated, setProductUpdated] = React.useState<string>("");
  const [bookingSystems, setBookingSystems] = React.useState<BookingSystem[]>([]);
  const [isExclusionLoading, setIsExclusionLoading] = React.useState(false);
  const [initialExcludedSystems, setInitialExcludedSystems] = React.useState<string[]>([]);
  
  const offeringDate = transferProduct?.offeringClosureDate;
  const isLinked = useMemo(() => {
    if (!offeringDate) return false;
    if (typeof offeringDate !== 'string') return false;
    const trimmed = offeringDate.trim();
    if (trimmed === "" || trimmed === "null" || trimmed === "undefined") return false;
    if (trimmed.startsWith("0001-01-01")) return false;
    const parsedDate = new Date(trimmed);
    if (isNaN(parsedDate.getTime())) return false;
    return true;
  }, [offeringDate]);
  const formDisabled = Boolean(isLinked || transferProduct?.entityUpdate);
  const portAndTransferTypeDisabled = Boolean(isLinked || transferProduct?.entityUpdate || transferProduct?.isLocked);
  const { activeDeactivationLocked } = useActivePackageDeactivationLock(
    transferProduct,
    TRANSFER_PRODUCT_TYPE_ID,
    DivisionId(),
    transferProduct
  );

  const transferTypeOptions = transferTypes
  .filter(i => i.id > 0)
  .map(i => ({
    value: Number(i.id),
    label: i.name,
  }));

const portOptions = transferPorts
  .filter(i => i.id > 0)
  .map(i => ({
    value: Number(i.id),
    label: i.name,
  }));

  const productCategoryOptions = productCategories
  .filter(i => i.id > 0)
  .map(i => ({
    value: Number(i.id),
    label: i.name,
  }));

   const accountCategoryOptions = accountCategories
  .filter(i => i.id > 0)
  .map(i => ({
    value: Number(i.id),
    label: i.name,
  }));

  useEffect(() => {
    let isMounted = true;
    api.getAllBookingSystems()
      .then(systems => {
        if (isMounted) {
          setBookingSystems(systems);
        }
      })
      .catch((err: any) => {
        message.error(err?.message ?? 'Failed to load booking systems');
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!transferProduct?.id) {
      form.resetFields();
      form.setFieldsValue({
        excludedBookingSystemIds: [],
        active: true,
      });
      return;
    }

    const active = transferProduct.active !== false;
    form.setFieldsValue({
      name: transferProduct.name || "",
      active,
      excludedBookingSystemIds: transferProduct?.excludedBookingSystemIds ?? [],
    });

    const hasOptions =
      transferTypeOptions.length > 0 &&
      portOptions.length > 0 &&
      productCategoryOptions.length > 0 &&
      accountCategoryOptions.length > 0;

    if (!hasOptions) {
      return;
    }

    const valuesToSet: Record<string, unknown> = {};

    if (transferProduct.transferTypeId && transferProduct.transferTypeId > 0) {
      valuesToSet.transferType = Number(transferProduct.transferTypeId);
    }
    
    if (transferProduct.portId && transferProduct.portId > 0) {
      valuesToSet.port = Number(transferProduct.portId);
    }
    
    if (transferProduct.productCategoryId && transferProduct.productCategoryId > 0) {
      valuesToSet.productCategoryId = Number(transferProduct.productCategoryId);
    }
    
    if (transferProduct.accountCategoryId && transferProduct.accountCategoryId > 0) {
      valuesToSet.accountCategoryId = Number(transferProduct.accountCategoryId);
    }
    
    form.setFieldsValue(valuesToSet);
  }, [
    transferProduct,
    transferTypeOptions.length,
    portOptions.length,
    productCategoryOptions.length,
    accountCategoryOptions.length,
    form,
  ]);

  useEffect(() => {
    if (activeDeactivationLocked) {
      form.setFieldsValue({ active: true });
    }
  }, [activeDeactivationLocked, form]);

  useEffect(() => {
    const productId = transferProduct?.id ?? 0;
    if (!productId) {
      setInitialExcludedSystems([]);
      form.setFieldsValue({ excludedBookingSystemIds: [] });
      setIsExclusionLoading(false);
      return;
    }

    let cancelled = false;
    setIsExclusionLoading(true);
    api.getExcludedBookingSystemIdsForProduct(productId, TRANSFER_PRODUCT_TYPE_ID)
      .then(ids => {
        if (!cancelled) {
          setInitialExcludedSystems(ids);
        }
      })
      .catch((err: any) => {
        if (!cancelled) {
          setInitialExcludedSystems([]);
          message.error(err?.message ?? 'Failed to load booking system exclusions');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsExclusionLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [transferProduct?.id, form]);

  useEffect(() => {
    form.setFieldsValue({ excludedBookingSystemIds: initialExcludedSystems });
  }, [initialExcludedSystems, form]);

  const toNum = (v: any) => (v === undefined || v === null ? NaN : Number(v));
      const pickId = (o: any) => toNum(o?.id ?? o?.courseIntensityId ?? o?.categoryId ?? o?.value);
      const pickName = (o: any) => (o?.name ?? o?.text ?? o?.title ?? o?.label ?? "");
  
      const nameById = React.useMemo(() => ({
        type: new Map(transferTypes.map(c => [pickId(c), pickName(c)])),
        port: new Map(transferPorts.map(c => [pickId(c), pickName(c)])),
        product:   new Map(productCategories.map(c => [pickId(c), pickName(c)])),
        account:   new Map(accountCategories.map(c => [pickId(c), pickName(c)])),
      }), [
        transferTypes,
        transferPorts,
        productCategories,
        accountCategories,
      ]);

  const handleFinish = async (values: any) => {
    if (formDisabled) {
      message.info("This transfer is linked to an offering and cannot be edited.");
      return;
    }

    setIsLoading(true);
    const selectedSystems = form.getFieldValue('excludedBookingSystemIds') as (string | number)[] | undefined;
    const draft = {
      ...transferProduct,
      ...values,
      active: activeDeactivationLocked ? true : values.active !== false,
      excludedBookingSystemIds: Array.isArray(selectedSystems)
        ? selectedSystems.map(id => String(id))
        : []
    } as TransfersProduct;
    dispatch(UpdateDraftTransferProduct(draft));

    try {
      const result = await api.saveTransferProduct(draft, DivisionId());
      if (!result?.ok) {
        setProductUpdated("Error " + (result?.message ?? 'Unknown error'));
        message.error("Error: " + (result?.message ?? 'Failed to save transfer'));
        return;
      }

      const savedProductIdRaw =
        result.productId ??
        draft.id ??
        transferProduct?.id ??
        0;
      const savedProductId = Number(savedProductIdRaw);

      if (savedProductId > 0) {
        try {
          const updatedIds = await api.updateBookingSystemProductExclusions(
            savedProductId,
            TRANSFER_PRODUCT_TYPE_ID,
            selectedSystems,
            initialExcludedSystems
          );
          setInitialExcludedSystems(updatedIds);
        } catch (err: any) {
          setProductUpdated("Error updating exclusions");
          message.error(err?.message ?? 'Failed to update booking system exclusions');
          return;
        }
      } else if (Array.isArray(selectedSystems) && selectedSystems.length > 0) {
        message.warning('Saved, but booking system exclusions could not be updated because the product id was unavailable.');
      }
      
      const safeList = await api.getTransferProducts(DivisionId()) || [];
      const itemIndex = safeList.findIndex(a => a.id === savedProductId);

      if(result?.productId && itemIndex === -1)
      {
        draft.id = savedProductId || 0;
        draft.active = true;
        draft.transferTypeText = nameById.type.get(Number(values.transferType)) ?? "";
        draft.portText = nameById.port.get(Number(values.port)) ?? "";
        draft.accountCategoryText = nameById.account.get(Number(values.accountCategoryId)) ?? "";
        draft.productCategoryText = nameById.product.get(Number(values.productCategoryId)) ?? "";
        draft.entityUpdate = true;
        
        safeList.push(draft);

        dispatch(listTransferssProductsData(safeList));
      }
      else
      {
          safeList[itemIndex] = { ...safeList[itemIndex], entityUpdate: true };
          dispatch(listTransferssProductsData(safeList));
      }

      setProductUpdated("Transfer product saved");
      onSuccess?.();
    } catch (error: any) {
      setProductUpdated("Error saving transfer product");
      message.error(error?.message ?? 'Failed to save transfer');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form
  form={form}
  layout="vertical"
  onFinish={handleFinish}
  autoComplete="off"
  style={{ maxWidth: 900, marginTop: 12 }}
  disabled={formDisabled}
   >
     {formDisabled && (
       <Row gutter={16} style={{ marginBottom: 16 }}>
         <Col span={24}>
           <Alert
             type="info"
             showIcon
             title="This transfer is linked to an offering and cannot be edited."
           />
         </Col>
       </Row>
     )}
  <Row gutter={16}>
    <Col span={12}>
      <Form.Item
        label="Name"
        name="name"
        rules={[{ required: true, message: "Please enter name" }]}
      >
        <Input maxLength={100} />
      </Form.Item>
    </Col>
    <Col span={12}>
      <Form.Item
        label="Transfer Type"
        name="transferType"
        rules={[{ required: true, message: "Please select transfer type" }]}
        tooltip={portAndTransferTypeDisabled ? "This field cannot be changed once the transfer is linked to an offering." : undefined}
        extra={portAndTransferTypeDisabled ? "This field cannot be changed once the transfer is linked to an offering." : undefined}
      >
        <Select
          key={`transferType-${transferProduct?.id || 'new'}-${transferTypeOptions.length}`}
          placeholder="Select Transfer Type"
          options={transferTypeOptions}
          showSearch={{ optionFilterProp: 'label' }}
          allowClear
          disabled={portAndTransferTypeDisabled}
        />
      </Form.Item>
    </Col>
  </Row>

  <Row gutter={16}>
    <Col span={12}>
      <Form.Item
        label="Port"
        name="port"
        rules={[{ required: true, message: "Please select port" }]}
        tooltip={portAndTransferTypeDisabled ? "This field cannot be changed once the transfer is linked to an offering." : undefined}
        extra={portAndTransferTypeDisabled ? "This field cannot be changed once the transfer is linked to an offering." : undefined}
      >
        <Select
          key={`port-${transferProduct?.id || 'new'}-${portOptions.length}`}
          placeholder="Select Port"
          options={portOptions}
          showSearch={{ optionFilterProp: 'label' }}
          allowClear
          disabled={portAndTransferTypeDisabled}
        />
      </Form.Item>
    </Col>
    <Col span={12}>
      <Form.Item
        label="Product Category"
        name="productCategoryId"
        rules={[{ required: true, message: "Please select product category" }]}
      >
        <Select
          key={`productCategory-${transferProduct?.id || 'new'}`}
          placeholder="Select Product Category"
          options={productCategoryOptions}
          showSearch={{ optionFilterProp: 'label' }}
          allowClear
        />
      </Form.Item>
    </Col>
  </Row>

  <Row gutter={16}>
    <Col span={12}>
      <Form.Item
        label="Account Category"
        name="accountCategoryId"
        rules={[{ required: true, message: "Please select account category" }]}
      >
        <Select
          key={`accountCategory-${transferProduct?.id || 'new'}`}
          placeholder="Select Account Category"
          options={accountCategoryOptions}
          showSearch={{ optionFilterProp: 'label' }}
          allowClear
        />
      </Form.Item>
    </Col>
    <Col span={12}></Col>
  </Row>

  <Row gutter={16}>
    <Col span={12}>
      <ActiveStatusFormItem
        name="active"
        tooltip={activeDeactivationLocked ? PRODUCT_ACTIVE_PACKAGE_TOOLTIP : undefined}
        disabled={formDisabled || activeDeactivationLocked}
      />
    </Col>
  </Row>
  <Row>
    <Col span={24}>
      <Form.Item
        name="excludedBookingSystemIds"
        label={
          <>
            <span style={{ fontWeight: 'bold', color: 'red', marginRight: 3 }}>Exclude</span>
            {' this transfer product from the following systems:'}
          </>
        }
      >
        <Checkbox.Group
          style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          disabled={formDisabled || isExclusionLoading}
        >
          {bookingSystems.map(system => (
            <Checkbox key={system.id} value={String(system.id)}>
              {system.name}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </Form.Item>
    </Col>
  </Row>

  <Form.Item style={{ textAlign: 'right' }}>
    <Button type="primary" htmlType="submit" loading={isLoading} disabled={formDisabled}>
      Save
    </Button>
    <span className="record-updated" style={{ marginLeft: 16 }}>
      {productUpdated}
    </span>
  </Form.Item>
</Form>
  );
};

export default AddEditTransferForm;